import React, { useState, useEffect, useMemo } from "react";
import { Box, Text, Spinner, Alert, AlertIcon, Center } from "@chakra-ui/react";

// --- REQUIRED CHART IMPORTS ---
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { BASE_URL } from "../../Globalapi/Globalapi"; // Assuming this is correct

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);
// --- END CHART IMPORTS ---

const academicId = sessionStorage.getItem("Academic");

// --- 1. Data Processing Function (MODIFIED) ---
/**
 * Transforms the flat exam_result array into the structured Chart.js data format.
 * It AGGREGATES (sums) the marks across ALL students in the class for each unique Subject/Exam.
 * * @param {Array} results - The filtered exam_result array from the API.
 * @returns {Object} - The structured data object for Chart.js.
 */
const formatChartData = (results) => {
    if (!results || results.length === 0) {
        return { labels: [], datasets: [] };
    }

    // Use a Map to accumulate totals for each unique subject/exam
    const aggregatedSubjectData = new Map();

    results.forEach((item) => {
        // Key to ensure uniqueness across subject and exam (e.g., "CE (mid term)")
        const key = `${item.subject} (${item.caption_exam})`;

        // Calculate marks for the current student/result
        const currentStudentTotalMarks =
            parseInt(item.written_mark || 0) + parseInt(item.internal_mark || 0);
        const currentPassMarks = parseInt(item.pass_marks || 0);
        const currentMaxMarks = parseInt(item.max_marks || 0);

        if (aggregatedSubjectData.has(key)) {
            // Accumulate marks if the subject/exam group already exists
            const existing = aggregatedSubjectData.get(key);
            
            existing.passMarks += currentPassMarks;
            existing.maxMarks += currentMaxMarks;
            existing.studentMarks += currentStudentTotalMarks;
            
            aggregatedSubjectData.set(key, existing);

        } else {
            // Initialize the entry for the new subject/exam group
            aggregatedSubjectData.set(key, {
                subject: key,
                passMarks: currentPassMarks,
                maxMarks: currentMaxMarks,
                studentMarks: currentStudentTotalMarks,
            });
        }
    });

    // Convert Map data into Chart.js structure
    const labels = [];
    const totalPassMarks = [];
    const totalMaxMarks = [];
    const totalStudentMarks = [];

    aggregatedSubjectData.forEach((data) => {
        labels.push(data.subject);
        totalPassMarks.push(data.passMarks);
        totalMaxMarks.push(data.maxMarks);
        totalStudentMarks.push(data.studentMarks);
    });

    return {
        labels: labels,
        datasets: [
            {
                label: "Total Class Pass Marks",
                data: totalPassMarks,
                backgroundColor: "rgba(255, 99, 132, 0.6)",
                borderColor: "rgba(255, 99, 132, 1)",
                borderWidth: 1,
            },
            {
                label: "Total Class Maximum Marks",
                data: totalMaxMarks,
                backgroundColor: "rgba(75, 192, 192, 0.6)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
            },
            {
                label: "Total Class Obtained Marks",
                data: totalStudentMarks,
                backgroundColor: "rgba(49, 130, 206, 0.8)",
                borderColor: "#3182CE",
                borderWidth: 1,
            },
        ],
    };
};

// --- 2. Chart Options ---
const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "top",
            labels: {
                usePointStyle: false,
                padding: 20,
                font: { family: "Poppins" },
            },
        },
        title: {
            display: true,
            text: "Exam Performance Comparison (Bar Chart)",
            font: { size: 16, family: "Poppins" },
            padding: 15,
        },
        tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 10,
        },
    },
    scales: {
        y: {
            min: 0,
            // To prevent bars from going off chart, dynamically calculate max if max_marks exceed 100
            // For now, keep it fixed to 100 as in your original code, but note the limitation.
            // You may need to replace this with: Math.max(...totalMaxMarks) + 10
            max: 1000, 
            title: { display: true, text: "Marks" },
            ticks: { stepSize: 100, font: { family: "Poppins" } },
            grid: { color: "rgba(200, 200, 200, 0.2)" },
        },
        x: {
            title: { display: true, text: "Subjects/Exams" },
            ticks: { font: { family: "Poppins" } },
            grid: { display: false },
        },
    },
    hover: { mode: "index", intersect: false },
};

// --- 3. React Component ---
function ExamResultsChart() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [examResults, setExamResults] = useState([]);
    const userId = sessionStorage.getItem("user_id");

    // --- Data Fetching Effect (Integrated here) ---
    useEffect(() => {
        if (!userId || !academicId) {
            setError("User ID or Academic ID is missing from session data.");
            return;
        }

        const fetchExamData = async () => {
            try {
                setLoading(true);

                const response = await fetch(
                    `${BASE_URL}/admin/exam/list_result_to_classteacher.php`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ pass: userId }),
                    }
                );

                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                const data = await response.json();

                if (data.exam_result && Array.isArray(data.exam_result)) {
                    // Filter by academic year
                    const filteredDetails = data.exam_result.filter(
                        (detail) => detail.academic_year_id === academicId
                    );
                    setExamResults(filteredDetails);
                } else {
                    setError(
                        data.message || "Failed to fetch results or invalid data format."
                    );
                }
            } catch (err) {
                setError(`Failed to fetch exam results: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchExamData();
    }, [userId, academicId]);

    // --- Chart Data Memoization ---
    const chartData = useMemo(() => formatChartData(examResults), [examResults]);

    // --- Render Logic ---
    if (loading) {
        return (
            <Box p={6} borderRadius="xs" boxShadow="md" bg="white">
                <Center h="400px">
                    <Spinner size="xl" color="blue.500" />
                    <Text ml={3}>Loading Exam Data...</Text>
                </Center>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={6} borderRadius="xs" boxShadow="md" bg="white">
                <Alert status="error">
                    <AlertIcon /> Error loading results: {error}
                </Alert>
            </Box>
        );
    }

    if (chartData.labels.length === 0) {
        return (
            <Box p={6} borderRadius="xs" boxShadow="md" bg="white">
                <Alert status="info">
                    <AlertIcon /> No exam results found for charting.
                </Alert>
            </Box>
        );
    }

    return (
        <Box bg="white" p={6} borderRadius="xs" boxShadow="md">
            <Text fontWeight="bold" fontSize="lg" mb={4} color="blue.600">
                Exam Results Comparison
            </Text>
            <div style={{ height: "400px", width: "100%" }}>
                <Bar data={chartData} options={chartOptions} />
            </div>
        </Box>
    );
}

export default ExamResultsChart;